import React, { useRef } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select/creatable';

const MultiEmailInput = ({
  value = '',
  onChange,
  label,
  placeholder = 'Enter email addresses...',
  required = false,
  name,
  options = [],
}) => {
  const selectRef = useRef();

  // Convert comma-separated string to array of objects for react-select
  const emails = value
    ? value.split(',')
      .filter(email => email.trim())
      .map(email => ({ value: email, label: email }))
    : [];

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleChange = (newValue) => {
    const emailString = newValue
      ? newValue.map(option => option.value).join(',')
      : '';
    onChange({ target: { name, value: emailString } });
  };

  const getOptionBackground = (isSelected, isFocused, data) => {
    if (data.__isNew__ && isValidEmail(data.value)) {
      return 'var(--bs-success)';
    }
    if (data.__isNew__ && !isValidEmail(data.value)) {
      return 'var(--bs-danger)';
    }
    if (isSelected) {
      return 'var(--bs-primary)';
    }
    if (isFocused) {
      return 'var(--bs-dark)';
    }
    return 'var(--bs-gray-900)';
  };

  const getNoOptionsMessage = ({ inputValue }) => {
    if (!inputValue) {
      return 'Type to add new email';
    }
    if (isValidEmail(inputValue)) {
      return 'Press enter to add email';
    }
    return 'Please enter a valid email address';
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: 'var(--bs-gray-900)',
      borderColor: 'var(--bs-gray-600)',
      '&:hover': {
        borderColor: 'var(--bs-primary)',
      },
    }),
    input: (base) => ({
      ...base,
      color: '#ffffff',
      '::placeholder': {
        color: 'var(--bs-gray-500)',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'var(--bs-gray-900)',
      border: '1px solid var(--bs-gray-600)',
    }),
    option: (base, { isSelected, isFocused, data }) => ({
      ...base,
      backgroundColor: getOptionBackground(isSelected, isFocused, data),
      color: '#ffffff',
      '&:hover': {
        backgroundColor: data.__isNew__ && !isValidEmail(data.value)
          ? 'var(--bs-danger)'
          : 'var(--bs-success)',
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--bs-gray-600)',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#ffffff',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'var(--bs-danger)',
        color: '#ffffff',
      },
    }),
  };

  return (
    <div className="multi-email-input">
      {label && (
        <Form.Label>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </Form.Label>
      )}
      <Select
        ref={selectRef}
        isMulti
        value={emails}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        styles={customStyles}
        className="email-input-container"
        classNamePrefix="email-select"
        formatCreateLabel={(inputValue) => (isValidEmail(inputValue)
          ? `Add "${inputValue}"`
          : `"${inputValue}" is not a valid email`)}
        noOptionsMessage={getNoOptionsMessage}
        components={{
          DropdownIndicator: null, // Remove the dropdown arrow
        }}
        onInputChange={(inputValue, { action }) => {
          // When user types a comma, try to add the email
          if (action === 'input-change' && inputValue.includes(',')) {
            const email = inputValue.replace(',', '').trim();
            if (isValidEmail(email)) {
              const newValue = [...emails, { value: email, label: email }];
              handleChange(newValue);
              return ''; // Clear the input
            }
          }
          return inputValue;
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && event.target.value) {
            const email = event.target.value.trim();
            if (isValidEmail(email)) {
              const newValue = [...emails, { value: email, label: email }];
              handleChange(newValue);
              event.preventDefault();
              // Clear the input by focusing and clearing the select
              if (selectRef.current) {
                selectRef.current.select.clearValue();
              }
            }
          }
        }}
      />
    </div>
  );
};

export default MultiEmailInput;
